/**
 * Navigation Fixes
 *
 * This file provides utilities to fix common issues that occur during navigation:
 * 1. ResizeObserver loop errors
 * 2. SVG attribute errors
 *
 * Implementation follows ConnectPath Methodology Module M15 for error handling
 * and Module M7 for integration points.
 */

// Track navigation state
let lastNavigationTime = Date.now();
let navigationCount = 0;
// Track pending timeout
let pendingFixTimeout: NodeJS.Timeout | null = null;
// Flag to prevent recursive fix applications
let isApplyingFixes = false;

/**
 * Enhanced function to fix SVG attributes with more edge cases
 */
export const fixSvgAttributes = (svg: SVGElement): void => {
  try {
    // Fix width attribute
    const width = svg.getAttribute('width');
    if (width === 'inherit' || width === 'undefined' || width === 'null') {
      svg.setAttribute('width', '100%');
    }
    
    // Fix height attribute
    const height = svg.getAttribute('height');
    if (height === 'inherit' || height === 'undefined' || height === 'null') {
      svg.setAttribute('height', '100%');
    }
    
    // Fix viewBox if missing
    if (!svg.getAttribute('viewBox') &&
        svg.getAttribute('width') &&
        svg.getAttribute('height')) {
      const w = parseFloat(svg.getAttribute('width') || '0');
      const h = parseFloat(svg.getAttribute('height') || '0');
      if (!isNaN(w) && !isNaN(h) && w > 0 && h > 0) {
        svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      }
    }
    
    // Fix preserveAspectRatio if needed
    if (!svg.getAttribute('preserveAspectRatio')) {
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    }
  } catch (error) {
    console.warn('Error fixing SVG attributes:', error);
  }
};

/**
 * Function to reapply SVG fixes after navigation
 */
export const reapplySvgFixes = (): void => {
  // Prevent recursive applications
  if (isApplyingFixes) {
    return;
  }

  try {
    isApplyingFixes = true;

    // Find all SVGs in the document
    const svgs = document.querySelectorAll('svg');

    // Fix each SVG's attributes
    svgs.forEach(svg => fixSvgAttributes(svg));

    // Use the global function if available
    if (window.__CONNECTPATH_UTILS?.fixAllSvgElements) {
      window.__CONNECTPATH_UTILS.fixAllSvgElements();
    }

    console.log(`SVG fixes reapplied to ${svgs.length} SVG elements after navigation`);
  } catch (error) {
    console.warn('Error reapplying SVG fixes:', error);
  } finally {
    // Reset the flag after a short delay to allow for legitimate future calls
    setTimeout(() => {
      isApplyingFixes = false;
    }, 250);
  }
};

/**
 * Function to reset ResizeObservers after navigation
 */
export const resetResizeObservers = (): void => {
  try {
    // Use the global function if available
    if (window.__CONNECTPATH_UTILS?.resetResizeObservers) {
      window.__CONNECTPATH_UTILS.resetResizeObservers();
      console.log('ResizeObservers reset after navigation');
    }
  } catch (error) {
    console.warn('Error resetting ResizeObservers:', error);
  }
};

/**
 * Function to suppress ResizeObserver errors after navigation
 */
export const suppressResizeObserverErrors = (): void => {
  try {
    // Check if resize-observer-patch.js already handled this
    if (window.__CONNECTPATH_RESIZE_OBSERVER_PATCHED) {
      console.log('Using resize-observer-patch.js error suppression');
      return;
    }
    
    // Use the global function if available
    if (window.__CONNECTPATH_UTILS?.suppressErrors) {
      // The error suppression is already set up in preload.js
      console.log('Using preload.js error suppression');
      return;
    }
    
    // Only add event listener as backup (no console.error override to avoid conflicts)
    window.addEventListener('error', function(event) {
      if (
        event &&
        event.message &&
        (event.message.includes('ResizeObserver loop') ||
         event.message.includes('ResizeObserver loop completed with undelivered notifications') ||
        (event.error && event.error.message && 
         (event.error.message.includes('ResizeObserver loop') ||
          event.error.message.includes('ResizeObserver loop completed with undelivered notifications'))))
      ) {
        // Prevent the error from bubbling up
        event.stopImmediatePropagation();
        event.preventDefault();
        return false;
      }
      
      // Check for SVG attribute errors
      if (
        event &&
        event.message &&
        (event.message.includes('<svg> attribute width') ||
         event.message.includes('<svg> attribute height') ||
         event.message.includes('Error: <svg'))
      ) {
        // Prevent the error from bubbling up
        event.stopImmediatePropagation();
        event.preventDefault();
        return false;
      }
      
      return true;
    }, true);
    
    console.log('ResizeObserver and SVG error suppression reapplied after navigation');
  } catch (error) {
    console.warn('Error suppressing errors:', error);
  }
};

/**
 * Function to reapply styled-components theme fixes after navigation
 */
export const reapplyThemeFixes = (): void => {
  try {
    // Ensure the default theme is still available
    if (
      typeof window !== 'undefined' &&
      window.__STYLED_COMPONENTS_DEFAULT_THEME__
    ) {
      // The theme is already set in the preload script
      // This is just to ensure it's still available after navigation
      console.log('Styled-components theme fix verified after navigation');
    } else if (typeof window !== 'undefined') {
      console.warn('Styled-components default theme not found, attempting to restore');
      
      // Create a minimal default theme if the original is missing
      window.__STYLED_COMPONENTS_DEFAULT_THEME__ = {
        colors: {
          primary: { 500: '#1890FF', 400: '#40A9FF', 600: '#096DD9' },
          secondary: { 500: '#722ED1', 400: '#9254DE', 600: '#531DAB' },
          neutral: {
            100: '#F5F5F5', 200: '#EEEEEE', 300: '#E0E0E0',
            400: '#BDBDBD', 500: '#9E9E9E', 600: '#757575',
            700: '#616161', 800: '#424242', 900: '#212121'
          },
          semantic: {
            success: { default: '#52C41A', light: '#B7EB8F', dark: '#389E0D' },
            error: { default: '#FF4D4F', light: '#FFCCC7', dark: '#CF1322' },
            warning: { default: '#FAAD14', light: '#FFE58F', dark: '#D48806' },
            info: { default: '#1890FF', light: '#91D5FF', dark: '#096DD9' }
          },
          theme: {
            background: { primary: '#FFFFFF', secondary: '#F9FAFB', tertiary: '#F3F4F6' },
            text: { primary: '#212121', secondary: '#616161', tertiary: '#9E9E9E' },
            border: { light: '#EEEEEE', medium: '#E0E0E0', dark: '#BDBDBD' },
          },
        },
        spacing: {
          xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px',
          '2xl': '48px', '3xl': '64px'
        },
        typography: {
          fontFamily: { primary: 'Inter, sans-serif', secondary: 'system-ui, sans-serif' },
          fontSize: {
            xs: '12px', sm: '14px', md: '16px', lg: '18px', xl: '20px',
            '2xl': '24px', '3xl': '30px', '4xl': '36px', '5xl': '48px'
          },
          fontWeight: {
            light: 300, regular: 400, medium: 500, semibold: 600, bold: 700
          },
          lineHeight: {
            none: '1', tight: '1.25', normal: '1.5', relaxed: '1.75', loose: '2'
          }
        },
        layout: {
          container: { sm: '640px', md: '768px', lg: '1024px', xl: '1280px' }
        },
        inset: { xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px' },
        borderRadius: { sm: '0.125rem', md: '0.25rem', lg: '0.5rem', xl: '1rem', full: '9999px' },
        shadows: {
          sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        },
        transitions: {
          fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
          medium: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
          slow: '500ms cubic-bezier(0.4, 0, 0.2, 1)'
        },
        breakpoints: {
          xs: '480px', sm: '640px', md: '768px', lg: '1024px', xl: '1280px'
        }
      };
    }
  } catch (error) {
    console.warn('Error reapplying theme fixes:', error);
  }
};

/**
 * Main function to reapply all fixes after navigation
 */
export const reapplyAllFixes = (): void => {
  // Update navigation state
  const now = Date.now();
  // Update last navigation time
  lastNavigationTime = now;
  navigationCount++;
  
  // Clear any pending fix timeout
  if (pendingFixTimeout) {
    clearTimeout(pendingFixTimeout);
    pendingFixTimeout = null;
  }
  
  // Apply fixes immediately
  const applyFixes = () => {
    try {
      // Reapply all fixes
      reapplySvgFixes();
      suppressResizeObserverErrors();
      reapplyThemeFixes();
      resetResizeObservers();
      
      // Notify the global utils if available
      if (window.__CONNECTPATH_UTILS?.onNavigation) {
        window.__CONNECTPATH_UTILS.onNavigation();
      }
      
      // Log success
      console.log(`All fixes reapplied after navigation (count: ${navigationCount})`);
    } catch (error) {
      console.warn('Error in reapplyAllFixes:', error);
    }
  };
  
  // Use requestAnimationFrame to ensure the DOM has updated
  requestAnimationFrame(() => {
    applyFixes();
    
    // Apply fixes again after a short delay to catch any late-rendered components
    pendingFixTimeout = setTimeout(() => {
      applyFixes();
      pendingFixTimeout = null;
    }, 300);
  });
};

/**
 * Function to initialize all fixes
 * This should be called once at application startup
 */
export const initializeAllFixes = (): void => {
  try {
    // Apply all fixes immediately
    reapplyAllFixes();
    
    // Set up navigation event listeners if using History API
    if (typeof window !== 'undefined' && window.history) {
      // Check if the global utils already set up the listeners
      if (!window.__CONNECTPATH_UTILS?.navigation) {
        const originalPushState = window.history.pushState;
        const originalReplaceState = window.history.replaceState;
        
        // Override pushState to reapply fixes after navigation
        window.history.pushState = function(...args) {
          const result = originalPushState.apply(this, args);
          reapplyAllFixes();
          return result;
        };
        
        // Override replaceState to reapply fixes after navigation
        window.history.replaceState = function(...args) {
          const result = originalReplaceState.apply(this, args);
          reapplyAllFixes();
          return result;
        };
        
        // Listen for popstate events (back/forward navigation)
        window.addEventListener('popstate', () => {
          reapplyAllFixes();
        });
        
        console.log('Navigation event listeners initialized');
      } else {
        console.log('Using preload.js navigation event listeners');
      }
    }
    
    // Set up MutationObserver to detect DOM changes
    if (typeof window !== 'undefined' && window.MutationObserver) {
      const observer = new MutationObserver((mutations) => {
        // Skip if we're already applying fixes
        if (isApplyingFixes) {
          return;
        }

        let hasSvgChanges = false;

        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            // Convert NodeList to Array before iterating
            const addedNodes = Array.from(mutation.addedNodes);
            for (const node of addedNodes) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Check if this is an SVG or contains SVGs
                // Also check if the SVG actually needs fixing to avoid unnecessary operations
                if ((node as Element).tagName === 'svg') {
                  const svg = node as SVGElement;
                  const width = svg.getAttribute('width');
                  const height = svg.getAttribute('height');
                  // Only consider it a change if the SVG has problematic attributes
                  if (width === 'inherit' || width === 'undefined' || width === 'null' ||
                      height === 'inherit' || height === 'undefined' || height === 'null') {
                    hasSvgChanges = true;
                    break;
                  }
                } else if ((node as Element).querySelectorAll) {
                  const svgs = (node as Element).querySelectorAll('svg');
                  for (let i = 0; i < svgs.length; i++) {
                    const svg = svgs[i];
                    const width = svg.getAttribute('width');
                    const height = svg.getAttribute('height');
                    if (width === 'inherit' || width === 'undefined' || width === 'null' ||
                        height === 'inherit' || height === 'undefined' || height === 'null') {
                      hasSvgChanges = true;
                      break;
                    }
                  }
                }
              }
            }
          }

          if (hasSvgChanges) break;
        }

        // If SVG elements were added that need fixing, reapply SVG fixes with debounce
        if (hasSvgChanges && !isApplyingFixes) {
          // Clear any pending timeout
          if (pendingFixTimeout) {
            clearTimeout(pendingFixTimeout);
          }

          // Debounce the SVG fixes to prevent rapid successive calls
          pendingFixTimeout = setTimeout(() => {
            if (!isApplyingFixes) { // Double-check before applying
              reapplySvgFixes();
            }
            pendingFixTimeout = null;
          }, 300); // Increased delay to 300ms
        }
      });

      // Start observing the document
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true
      });
      
      console.log('DOM mutation observer initialized');
    }
    
    console.log('All fixes initialized');
  } catch (error) {
    console.warn('Error initializing fixes:', error);
  }
};

// Export a named object for better ESLint compatibility
const navigationFixes = {
  fixSvgAttributes,
  reapplySvgFixes,
  suppressResizeObserverErrors,
  resetResizeObservers,
  reapplyThemeFixes,
  reapplyAllFixes,
  initializeAllFixes
};

export default navigationFixes;